import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight, MapPin } from "lucide-react";
import { FL } from "@/constants/testIds/fairylights";

/**
 * Kinetic hero — split composition:
 *   • Left column: masked line-by-line reveal of the ceremonial headline.
 *   • Right column: the brand mark treated as an editorial spotlight
 *     (clipped-frame, parallax float, radial gold glow behind).
 */
const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      delay: 0.2 + i * 0.09,
      duration: 1.15,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.95 + i * 0.08, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ heroImage, onCta, onMessage }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#1A050B]"
    >
      {/* Ambient burgundy → black gradient with radial gold glow */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#4A0E1F] via-[#2a0812] to-[#1A050B]" />
      <div className="pointer-events-none absolute right-[-10%] top-[10%] h-[70%] w-[70%] rounded-full bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.22),transparent_60%)]" />
      <div className="pointer-events-none absolute left-[-20%] bottom-[-20%] h-[60%] w-[60%] rounded-full bg-[radial-gradient(circle_at_center,rgba(74,14,31,0.6),transparent_70%)]" />

      {/* Sparkles */}
      <div className="pointer-events-none absolute inset-0 z-[2]">
        <span className="sparkle" style={{ top: "18%", left: "14%" }} />
        <span className="sparkle" style={{ top: "72%", left: "62%", animationDelay: "0.8s" }} />
        <span className="sparkle" style={{ top: "34%", left: "82%", animationDelay: "1.7s" }} />
        <span className="sparkle" style={{ top: "58%", left: "22%", animationDelay: "2.4s" }} />
      </div>

      {/* Top strip */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-8 sm:px-10 md:px-14">
        <div className="flex items-center gap-3">
          <span className="font-serif text-2xl italic text-[#D4AF37]">f</span>
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#FDFBF7]/70">
            Est. Dubai · 2015
          </span>
        </div>
        <div className="hidden items-center gap-6 md:flex text-[11px] uppercase tracking-[0.28em] text-[#FDFBF7]/70">
          <a href="#work" className="hover:text-[#D4AF37] transition-colors">Work</a>
          <a href="#chapters" className="hover:text-[#D4AF37] transition-colors">Manifesto</a>
          <a href="#contact" className="hover:text-[#D4AF37] transition-colors">Contact</a>
        </div>
      </div>

      {/* Hero body */}
      <div className="relative z-10 mx-auto grid min-h-[calc(100svh-140px)] max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-16 pt-12 sm:px-10 md:grid-cols-[1.05fr_1fr] md:gap-16 md:px-14 md:pb-24 md:pt-16">
        {/* LEFT — kinetic headline */}
        <motion.div initial="hidden" animate="show" className="relative">
          <div className="mb-8 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
              Chapter 00 · The Atelier
            </span>
          </div>

          <h1 className="font-serif text-[16vw] leading-[0.95] tracking-[-0.02em] text-[#FDFBF7] sm:text-[12vw] md:text-[6.6vw] lg:text-[5.6vw]">
            <span className="mask-line">
              <motion.span variants={line} custom={0} className="block">
                Weddings that
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={1} className="block italic text-[#D4AF37]">
                remember you
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={2} className="block">
                long after
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={3} className="block font-italic-serif">
                <em>the last candle.</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="mt-8 max-w-lg font-body text-[15px] leading-[1.7] text-[#FDFBF7]/80"
          >
            A Dubai-based atelier of light, bloom & ceremony. We compose weddings,
            galas and private moments for people who notice how a room breathes.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid={FL.heroCta}
              onClick={onCta}
              className="pill pill-primary group"
            >
              Begin an enquiry
              <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <button
              data-testid={FL.heroCtaMessage}
              onClick={onMessage}
              className="pill pill-ghost"
            >
              <MapPin className="h-3.5 w-3.5" />
              Dubai, UAE
            </button>
          </motion.div>
        </motion.div>

        {/* RIGHT — brand mark spotlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: yImg, opacity }}
          className="relative mx-auto w-full max-w-[520px] md:max-w-none"
        >
          {/* Radial gold aura */}
          <div className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.28),transparent_65%)] blur-2xl" />

          <motion.div
            style={{ scale }}
            className="relative aspect-square overflow-hidden border border-[#D4AF37]/30 bg-black shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9),0_0_60px_-10px_rgba(212,175,55,0.35)]"
          >
            <img
              src={heroImage}
              alt="Fairy Light Party and Events — brand mark"
              className="h-full w-full object-cover"
            />

            {/* Editorial corner marks */}
            <span className="absolute left-4 top-4 h-8 w-px bg-[#D4AF37]/70" />
            <span className="absolute left-4 top-4 h-px w-8 bg-[#D4AF37]/70" />
            <span className="absolute right-4 top-4 h-8 w-px bg-[#D4AF37]/70" />
            <span className="absolute right-4 top-4 h-px w-8 bg-[#D4AF37]/70" />
            <span className="absolute left-4 bottom-4 h-8 w-px bg-[#D4AF37]/70" />
            <span className="absolute left-4 bottom-4 h-px w-8 bg-[#D4AF37]/70" />
            <span className="absolute right-4 bottom-4 h-8 w-px bg-[#D4AF37]/70" />
            <span className="absolute right-4 bottom-4 h-px w-8 bg-[#D4AF37]/70" />
          </motion.div>

          {/* Caption plate below the mark */}
          <div className="mt-6 flex items-center justify-between border-t border-[#D4AF37]/25 pt-4 text-[11px] uppercase tracking-[0.24em] text-[#FDFBF7]/60">
            <span>The Mark · 2015 →</span>
            <span className="text-[#D4AF37]">We create moments, you cherish forever.</span>
          </div>
        </motion.div>
      </div>

      {/* Bottom strip */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={3}
        className="relative z-10 mx-auto flex max-w-7xl items-center justify-between border-t border-[#D4AF37]/25 px-6 py-6 text-[11px] uppercase tracking-[0.28em] text-[#FDFBF7]/50 sm:px-10 md:px-14"
      >
        <span>Scroll — the profile begins</span>
        <span className="hidden md:inline">Est. Dubai · 2015</span>
      </motion.div>
    </section>
  );
}
